import { useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { useMobileMoreMenu } from "../../app/components/mobile-more-menu-context"
import { useAcknowledgeChat } from "../../app/hooks/use-acknowledge-chat"
import { useAutoAckOnArrival } from "../../app/hooks/use-auto-ack-on-arrival"
import { useBottomVisibleTrigger } from "../../app/hooks/use-bottom-visible-trigger"
import { useChat } from "../../app/hooks/use-chat"
import {
	type MareczekThinkingState,
	setMareczekThinking,
	useMareczekThinking,
} from "../../app/hooks/use-mareczek-thinking"
import { useProperty } from "../../app/hooks/use-property"
import { usePropertyMemberships } from "../../app/hooks/use-property-memberships"
import { usePropertyUserChatChannel } from "../../app/hooks/use-property-user-chat-channel"
import { useSharedChatChannel } from "../../app/hooks/use-shared-chat-channel"
import { useStickyNonZero } from "../../app/hooks/use-sticky-nonzero"
import { useTopVisibleTrigger } from "../../app/hooks/use-top-visible-trigger"
import { getTranslations } from "../../app/i18n"
import { useLocale } from "../../app/locale-context"
import { useUser } from "../../app/user-context"
import { parseStatusMarker } from "../../lib/chat-status"
import { getOwnedOrigins } from "../../lib/owned-origins.runtime"
import { renderChatMessage } from "../../lib/render-chat-message"
import { formatFullDateTime, formatShortTime } from "../../lib/user-display"
import { cn } from "../../lib/utils"
import { AgentActionCard } from "./agent-action-card"
import {
	CHAT_COMPOSER_FIELD_CLASS,
	CHAT_SCROLL_AREA_CLASS,
	ChatSendButton,
	chatBubbleClass,
} from "./chat-bubble"
import { ChatUnreadBadge } from "./chat-unread-badge"
import { MareczekAvatar } from "./mareczek-avatar"
import { MessageMarkdown } from "./message-markdown"
import { ThinkingBubble } from "./thinking-bubble"
import { type OpenClosePhase, useOpenClosePhase } from "./use-open-close-phase"

function MinusIcon({ size = 18 }: { size?: number }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M5 12h14" />
		</svg>
	)
}

function ExpandIcon({ size = 16 }: { size?: number }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M15 3h6v6" />
			<path d="M9 21H3v-6" />
			<path d="M21 3l-7 7" />
			<path d="M3 21l7-7" />
		</svg>
	)
}

function CollapseIcon({ size = 16 }: { size?: number }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M4 14h6v6" />
			<path d="M20 10h-6V4" />
			<path d="M14 10l7-7" />
			<path d="M3 21l7-7" />
		</svg>
	)
}

interface PropertyChatProps {
	propertyId: string
	/**
	 * When true the floating button and any open panel are suppressed —
	 * the members-chat rail uses this so the two chat surfaces don't
	 * compete for the right side of the screen.
	 */
	hidden?: boolean
	/**
	 * Current route's projectId, set when the private chat is shown on a
	 * project page. Travels in the send body's `context` so Mareczek knows
	 * which project the user is looking at. Does NOT change the endpoint —
	 * the private chat stays property-scoped.
	 */
	contextProjectId?: string
}

export function PropertyChat({
	propertyId,
	hidden = false,
	contextProjectId,
}: PropertyChatProps) {
	const {
		phase,
		open: openPanel,
		close: closePanel,
		closeInstantly,
	} = useOpenClosePhase()
	const { locale } = useLocale()
	const t = getTranslations(locale).chat
	const { profile } = useUser()
	const myUserId = profile?.id ?? null

	// Mount both Pusher channels at the outer scope so badge updates arrive
	// even when the chat panel is closed.
	usePropertyUserChatChannel(propertyId, myUserId ?? undefined)
	useSharedChatChannel(propertyId, myUserId ?? undefined)

	// Resolve private chatId from PropertyDTO.chats
	const { data: property } = useProperty(propertyId)
	const privateChat = property?.chats?.find((c) => c.kind === "private")
	const privateChatId = privateChat?.id
	const unreadCount = useStickyNonZero(
		privateChat?.unreadMessageCount ?? 0,
		5000,
	)

	const { state, sendMessage, loadOlder, hasOlder, loadingOlder } = useChat(
		propertyId,
		privateChatId,
		undefined,
		contextProjectId,
	)

	const queryClient = useQueryClient()
	const thinking = useMareczekThinking(privateChatId)

	// Safety net: if both the reply and the `thinking-cleared` event go missing,
	// don't let the indicator spin forever.
	useEffect(() => {
		if (!thinking.active || !privateChatId) return
		const id = window.setTimeout(
			() => setMareczekThinking(queryClient, privateChatId, false),
			90_000,
		)
		return () => window.clearTimeout(id)
	}, [thinking, privateChatId, queryClient])

	// Show the indicator the moment the user sends — covers a direct reply that
	// uses no tools (the API only emits `mareczek.thinking` around tool calls).
	const handleSend = async (content: string) => {
		if (privateChatId) setMareczekThinking(queryClient, privateChatId, true)
		try {
			await sendMessage(content)
		} catch (err) {
			if (privateChatId) setMareczekThinking(queryClient, privateChatId, false)
			throw err
		}
	}

	const { moreOpen } = useMobileMoreMenu()

	useEffect(() => {
		if ((moreOpen || hidden) && phase !== "closed") closeInstantly()
	}, [moreOpen, hidden, phase, closeInstantly])

	if (moreOpen || hidden) return null

	// The FAB stays mounted and cross-fades in counterpoint to the panel, so
	// the swap reads as one continuous gesture (ZPL-57).
	const panelUp = phase === "opening" || phase === "open"
	return (
		<>
			<div
				className={cn(
					"fixed bottom-[5.5rem] right-6 md:bottom-6 z-40 group",
					"transition-opacity duration-200 motion-reduce:transition-none",
					panelUp ? "opacity-0 pointer-events-none" : "opacity-100",
				)}
				aria-hidden={panelUp}
			>
				<span
					role="tooltip"
					id="mareczek-tooltip"
					className="absolute bottom-full right-0 mb-2 px-2.5 py-1 rounded-md bg-popover text-popover-foreground text-xs whitespace-nowrap shadow opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity pointer-events-none"
				>
					{t.openButtonLabel}
				</span>
				<button
					type="button"
					onClick={openPanel}
					tabIndex={panelUp ? -1 : 0}
					aria-label={t.openButtonLabel}
					aria-describedby="mareczek-tooltip"
					className="relative h-10 w-10 md:h-14 md:w-14 rounded-full shadow-lg hover:shadow-xl transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring bg-transparent"
				>
					<span className="md:hidden">
						<MareczekAvatar size={40} badgeFontSize={10} />
					</span>
					<span className="hidden md:inline-flex">
						<MareczekAvatar size={56} />
					</span>
					<span className="absolute -top-1 -right-1">
						<ChatUnreadBadge count={unreadCount} />
					</span>
				</button>
			</div>
			{phase !== "closed" && (
				<ChatPanel
					t={t}
					state={state}
					propertyId={propertyId}
					privateChatId={privateChatId}
					myUserId={myUserId}
					thinking={thinking}
					phase={phase}
					onClose={closePanel}
					onSend={handleSend}
					loadOlder={loadOlder}
					hasOlder={hasOlder}
					loadingOlder={loadingOlder}
				/>
			)}
		</>
	)
}

type ChatT = ReturnType<typeof getTranslations>["chat"]
type ChatState = ReturnType<typeof useChat>["state"]

interface ChatPanelProps {
	t: ChatT
	state: ChatState
	propertyId: string
	privateChatId: string | undefined
	myUserId: string | null
	thinking: MareczekThinkingState
	/** Drives the mount/unmount transition classes (ZPL-57). */
	phase: OpenClosePhase
	onClose: () => void
	onSend: (content: string) => Promise<void>
	loadOlder: () => void
	hasOlder: boolean
	loadingOlder: boolean
}

function ChatPanel({
	t,
	state,
	propertyId,
	privateChatId,
	myUserId,
	thinking,
	phase,
	onClose,
	onSend,
	loadOlder,
	hasOlder,
	loadingOlder,
}: ChatPanelProps) {
	const { locale } = useLocale()
	// Read once here (the chat container runs in real Vite); MessageMarkdown stays
	// import.meta-free by receiving owned origins as a prop.
	const ownedOrigins = useMemo(getOwnedOrigins, [])
	const tAll = getTranslations(locale)
	// Memberships back the userId-→-name lookup for templated Mareczek
	// messages (e.g. propertyJoined). Cached + Pusher-invalidated, so this
	// is free once the property page mounted.
	const { data: memberships } = usePropertyMemberships(propertyId)
	const resolveUser = (id: string) => {
		if (memberships?.owner?.id === id) return memberships.owner
		const m = memberships?.memberships.find((x) => x.user.id === id)
		return m?.user ?? null
	}
	const bodyRef = useRef<HTMLDivElement>(null)
	const topSentinelRef = useRef<HTMLDivElement | null>(null)
	useTopVisibleTrigger({
		sentinelRef: topSentinelRef,
		enabled: state.status === "ready" && hasOlder,
		rearmKey: state.status === "ready" ? state.messages.length : 0,
		onVisible: loadOlder,
	})
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const [draft, setDraft] = useState("")
	const [sending, setSending] = useState(false)
	const [sendError, setSendError] = useState<string | null>(null)
	// Desktop-only expand toggle. Lives here (not in PropertyChat) so it resets
	// to the default size every time the panel is opened — ChatPanel unmounts on
	// close. Below 480px the panel is already fullscreen, so the toggle is hidden
	// and this state has no visible effect.
	const [expanded, setExpanded] = useState(false)

	// biome-ignore lint/correctness/useExhaustiveDependencies: draft change must re-fire so the textarea re-measures scrollHeight; the function body reads no React state directly.
	useEffect(() => {
		const el = textareaRef.current
		if (!el) return
		el.style.height = "auto"
		el.style.height = `${Math.min(el.scrollHeight, 250)}px`
	}, [draft])

	const wasSendingRef = useRef(sending)
	useEffect(() => {
		if (wasSendingRef.current && !sending) {
			textareaRef.current?.focus()
		}
		wasSendingRef.current = sending
	}, [sending])

	// ChatPanel only mounts when the user opens it (PropertyChat returns the
	// floating button vs. <ChatPanel/> based on `open`), so this fires once
	// per opening and lands the cursor in the composer.
	useEffect(() => {
		textareaRef.current?.focus()
	}, [])

	const { acknowledge } = useAcknowledgeChat(propertyId, privateChatId)
	const newest = state.status === "ready" ? (state.messages[0] ?? null) : null
	const newestId = newest?.id ?? null
	const newestActorIsSelf =
		newest?.actorType === "user" && newest?.actorId === myUserId
	// Snap to the bottom only when the NEWEST message changes — loading an
	// older page must not yank the user back down.
	useEffect(() => {
		if (!newestId) return
		const el = bodyRef.current
		if (!el) return
		el.scrollTop = el.scrollHeight
	}, [newestId])
	useBottomVisibleTrigger({
		scrollRef: bodyRef,
		enabled:
			state.status === "ready" &&
			newestId !== null &&
			privateChatId !== undefined,
		onSettled: () => {
			if (newestId) acknowledge(newestId)
		},
	})
	// Panel is only mounted when open — no inViewRef needed; the mount is the visibility signal.
	useAutoAckOnArrival({
		enabled: privateChatId !== undefined,
		newestMessageId: newestId,
		newestActorIsSelf,
		onAck: acknowledge,
	})

	const submit = async (e?: FormEvent) => {
		e?.preventDefault()
		const content = draft.trim()
		if (!content || sending) return
		setSending(true)
		setSendError(null)
		try {
			await onSend(content)
			setDraft("")
			if (textareaRef.current) {
				textareaRef.current.style.height = "auto"
			}
		} catch (err) {
			setSendError(err instanceof Error ? err.message : t.errors.sendFailed)
		} finally {
			setSending(false)
		}
	}

	return (
		<div
			className={cn(
				"fixed z-40 bg-popover text-popover-foreground shadow-xl border border-border flex flex-col overflow-hidden",
				expanded
					? "inset-4 w-auto h-auto rounded-[24px]"
					: "bottom-[5.5rem] right-6 md:bottom-6 w-[320px] h-[400px] rounded-[24px]",
				"max-[480px]:inset-0 max-[480px]:w-screen max-[480px]:h-[100dvh] max-[480px]:rounded-none max-[480px]:right-0 max-[480px]:bottom-0",
				// ZPL-57: transform+opacity only (compositor-friendly — never
				// transition width/height). Desktop scales from the FAB corner;
				// ≤480px slides up from the bottom edge like a native sheet.
				"transform-gpu origin-bottom-right transition-[opacity,transform] duration-200 motion-reduce:transition-none",
				phase === "open"
					? "ease-out opacity-100 scale-100 max-[480px]:translate-y-0"
					: "ease-in opacity-0 scale-95 max-[480px]:opacity-100 max-[480px]:scale-100 max-[480px]:translate-y-full",
			)}
		>
			<header className="flex items-center justify-between px-3 py-2 border-b border-border">
				<div className="flex items-center gap-2">
					<MareczekAvatar size={32} showBadge={false} />
					<div className="leading-tight">
						{/* Design hierarchy (MainLayout): mono-caps brand eyebrow over
						    the bold role line — existing strings, no new copy. */}
						<div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
							{t.headerName} AI
						</div>
						<div className="font-semibold text-sm text-foreground">
							{t.headerSubtitle}
						</div>
					</div>
				</div>
				<div className="flex items-center gap-0.5">
					<button
						type="button"
						onClick={() => setExpanded((v) => !v)}
						aria-label={expanded ? t.collapseButtonLabel : t.expandButtonLabel}
						aria-pressed={expanded}
						className="hidden md:flex text-muted-foreground/80 hover:text-foreground hover:bg-accent transition-colors cursor-pointer h-7 w-7 items-center justify-center rounded-md"
					>
						{expanded ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
					</button>
					<button
						type="button"
						onClick={onClose}
						aria-label={t.minimizeButtonLabel}
						className="text-muted-foreground/80 hover:text-foreground hover:bg-accent transition-colors cursor-pointer h-7 w-7 flex items-center justify-center rounded-md"
					>
						<MinusIcon size={18} />
					</button>
				</div>
			</header>

			<div
				ref={bodyRef}
				className={cn(
					"flex-1 overflow-y-auto overflow-x-hidden p-3 flex flex-col-reverse gap-2 text-sm",
					CHAT_SCROLL_AREA_CLASS,
				)}
			>
				{thinking.active && (
					<ThinkingBubble label={t.thinkingLabel} text={thinking.text} />
				)}
				{state.status === "loading" && (
					<div className="text-muted-foreground text-xs text-center m-auto">
						…
					</div>
				)}
				{state.status === "error" && (
					<div className="text-destructive text-xs text-center m-auto">
						{state.message}
					</div>
				)}
				{state.status === "ready" && state.messages.length === 0 && (
					<div className="text-muted-foreground text-xs text-center m-auto">
						{t.emptyState}
					</div>
				)}
				{state.status === "ready" &&
					state.messages.map((m) => {
						const mine = m.actorType === "user" && m.actorId === myUserId
						const shortTime = formatShortTime(m.createdAt, locale)
						const fullTime = formatFullDateTime(m.createdAt, locale)
						if ("agentAction" in m.content && privateChatId) {
							// Mareczek's mutation-approval card (ZPL-122/123). The row is
							// full-width (no self-start): the bubble's percentage max-width then
							// resolves against the chat column, so the bubble hugs its
							// content instead of capping at 75% of its own natural width
							// (which wrapped the CTA labels mid-word).
							return (
								<div key={m.id} className="flex items-end gap-1.5">
									<div className={chatBubbleClass("ai")}>
										<AgentActionCard
											action={m.content.agentAction}
											propertyId={propertyId}
											chatId={privateChatId}
											t={tAll}
										/>
									</div>
									<time
										dateTime={m.createdAt}
										title={fullTime}
										className="text-[10px] text-muted-foreground leading-none pb-1 tabular-nums"
									>
										{shortTime}
									</time>
								</div>
							)
						}
						const text = renderChatMessage(m.content, { t: tAll, resolveUser })
						// Mareczek replies render as markdown; a legacy FAILURE marker
						// (stripped here) flags the bubble red. The user's own messages
						// stay plain text.
						const isAssistant = m.actorType !== "user"
						const { status, body } = isAssistant
							? parseStatusMarker(text)
							: { status: null, body: text }
						const failed = status === "failure"
						// Full-width row; `mine` packs right via row-reverse. A
						// self-start/self-end (fit-content) row makes the bubble's
						// percentage max-width resolve against the bubble itself.
						return (
							<div
								key={m.id}
								className={cn(
									"flex items-end gap-1.5",
									mine && "flex-row-reverse",
								)}
							>
								<div
									className={chatBubbleClass(
										isAssistant ? "ai" : mine ? "own" : "other",
										{
											failed,
										},
									)}
								>
									{isAssistant ? (
										<MessageMarkdown ownedOrigins={ownedOrigins}>
											{body}
										</MessageMarkdown>
									) : (
										text
									)}
								</div>
								<time
									dateTime={m.createdAt}
									title={fullTime}
									className="text-[10px] text-muted-foreground leading-none pb-1 tabular-nums"
								>
									{shortTime}
								</time>
							</div>
						)
					})}
				{state.status === "ready" && loadingOlder && (
					<div className="text-muted-foreground text-xs text-center py-1">
						…
					</div>
				)}
				{state.status === "ready" && hasOlder && (
					<div ref={topSentinelRef} className="h-px shrink-0" />
				)}
			</div>

			{sendError && (
				<div className="text-xs text-destructive px-3 py-1 bg-destructive/10 border-t border-destructive/30">
					{sendError}
				</div>
			)}

			<form
				onSubmit={submit}
				className="flex gap-2 p-2 border-t border-border bg-card"
			>
				<textarea
					ref={textareaRef}
					value={draft}
					onChange={(e) => setDraft(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault()
							submit()
						}
					}}
					placeholder={t.inputPlaceholder}
					disabled={sending}
					rows={1}
					className={cn("flex-1", CHAT_COMPOSER_FIELD_CLASS)}
				/>
				<ChatSendButton
					disabled={sending || !draft.trim()}
					label={t.sendButtonLabel}
				/>
			</form>
		</div>
	)
}
